import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import SlickImageGallery from 'legacyfrontend/components/slick-image-gallery';
import RefreshModal from 'legacyfrontend/components/home/components/RefreshModal';
import Dialog from '@mui/material/Dialog';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  getHotelDetailRemainingMs,
  readHotelDetailState,
  saveHotelDetail,
  canRevalidateDetail,
} from '../utils/hotelDetailStorage';
import type { HotelDetailState } from '../utils/hotelDetailStorage';
import { calculateNights } from '../utils/nightsCalculation';
import { HotelDetailContent } from './HotelDetailContent';
import { HotelBookingForm } from './HotelBookingForm';
import { resolveBookingRoomLayout } from '../utils/buildBookingRooms';
import { HotelImagePreview } from './HotelImagePreview';
import type { HotelDetailData } from '../types/hotels.presentation';
import { formatHotelPrice } from '../utils/formatHotelPrice';
import { useHotelBooking } from '../hooks/useHotelBooking';
import type { BookingSelection, RatePolicyMode } from '../hooks/useHotelBooking';
import { createHotelImagePlaceholderElement, HotelImagePlaceholder } from './HotelImagePlaceholder';
import { useListingColors } from 'legacyfrontend/hooks/useListingColors';
import {
  LISTING_PRIMARY_BUTTON_CLASS,
  listingPrimaryButtonStyle,
} from 'legacyfrontend/hooks/listingPrimaryButton';

const STAR_SYMBOL = '★';

type RefreshModalProps = {
  open: boolean;
  onConfirm: () => void;
  title?: string;
  message?: string;
  detail?: string;
  confirmLabel?: string;
  disabled?: boolean;
};

const RefreshModalComponent = RefreshModal as React.ComponentType<RefreshModalProps>;

interface GalleryProps {
  images: string[];
  alt: string;
  noImagesLabel: string;
  galleryLabel: string;
  previousImageLabel: string;
  nextImageLabel: string;
  imageUnavailableLabel: string;
}

type SlickSliderController = {
  slickPrev: () => void;
  slickNext: () => void;
};

function Gallery({
  images,
  alt,
  noImagesLabel,
  galleryLabel,
  previousImageLabel,
  nextImageLabel,
  imageUnavailableLabel,
}: GalleryProps) {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [isKeyboardControlActive, setIsKeyboardControlActive] = useState(false);
  const currentSlideRef = useRef(0);
  const sliderRef = useRef<SlickSliderController | null>(null);
  const galleryContainerRef = useRef<HTMLDivElement | null>(null);

  const handleSlideChange = useCallback((newIndex: number) => {
    currentSlideRef.current = newIndex;
  }, []);

  const handleGalleryClick = useCallback(() => {
    setPreviewIndex(currentSlideRef.current);
  }, []);

  const handleImageClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('button, .custom-slick-arrow, .slick-dots')) return;

      const clickedImage = target.closest('img');
      if (!clickedImage) return;

      handleGalleryClick();
    },
    [handleGalleryClick],
  );

  const moveSlideWithKey = useCallback((key: string) => {
    if (key === 'ArrowLeft') {
      sliderRef.current?.slickPrev();
      return;
    }

    sliderRef.current?.slickNext();
  }, []);

  const handleGalleryKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      moveSlideWithKey(event.key);
    },
    [moveSlideWithKey],
  );

  const handlePreviousImage = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    sliderRef.current?.slickPrev();
  }, []);

  const handleNextImage = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    sliderRef.current?.slickNext();
  }, []);

  useEffect(() => {
    if (!isKeyboardControlActive || previewIndex != null) return undefined;

    const handleWindowKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      moveSlideWithKey(event.key);
    };

    window.addEventListener('keydown', handleWindowKeyDown);
    return () => window.removeEventListener('keydown', handleWindowKeyDown);
  }, [isKeyboardControlActive, moveSlideWithKey, previewIndex]);

  const handleClosePreview = useCallback(() => {
    setPreviewIndex(null);
  }, []);

  // Delegated image error handler: replaces broken gallery <img> elements
  // with a placeholder while keeping the slider navigation functional.
  useEffect(() => {
    const container = galleryContainerRef.current;
    if (!container) return;

    const handleImgError = (e: Event) => {
      const img = e.target as HTMLImageElement | null;
      if (!img) return;

      const placeholder = createHotelImagePlaceholderElement(imageUnavailableLabel);
      img.replaceWith(placeholder);
    };

    const attachToImages = () => {
      const imgs = container.querySelectorAll('img');
      imgs.forEach((img) => {
        if (!img.hasAttribute('data-gallery-error-bound')) {
          img.setAttribute('data-gallery-error-bound', 'true');
          img.addEventListener('error', handleImgError);
        }
      });
    };

    // Attach immediately and watch for DOM changes (new slides in virtualized slider).
    attachToImages();
    const observer = new MutationObserver(attachToImages);
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      const imgs = container.querySelectorAll('img');
      imgs.forEach((img) => img.removeEventListener('error', handleImgError));
    };
  }, [imageUnavailableLabel]);

  if (!images.length) {
    return (
      <section aria-label={galleryLabel}>
        <HotelImagePlaceholder
          className='flex aspect-[4/3] w-full items-center justify-center rounded-lg bg-gray-200'
          iconClassName='mx-auto h-12 w-12 text-gray-400'
          label={imageUnavailableLabel}
          text={noImagesLabel}
        />
      </section>
    );
  }

  return (
    <>
      <section aria-label={galleryLabel}>
        <div
          aria-label={galleryLabel}
          className='hotel-detail-gallery group relative overflow-hidden rounded-md bg-gray-100'
          onBlur={() => setIsKeyboardControlActive(false)}
          onClick={handleImageClick}
          onFocus={() => setIsKeyboardControlActive(true)}
          onKeyDown={handleGalleryKeyDown}
          onMouseEnter={() => setIsKeyboardControlActive(true)}
          onMouseLeave={() => setIsKeyboardControlActive(false)}
          ref={galleryContainerRef}
          role='group'
          tabIndex={0}
        >
          <SlickImageGallery
            customSettings={{ afterChange: handleSlideChange, arrows: false }}
            items={images}
            sliderRef={sliderRef}
            title={alt}
          />
          {images.length > 1 ? (
            <>
              <button
                aria-label={previousImageLabel}
                className='hotel-detail-gallery-control hotel-detail-gallery-control--prev'
                onClick={handlePreviousImage}
                type='button'
              >
                <span aria-hidden='true'>‹</span>
              </button>
              <button
                aria-label={nextImageLabel}
                className='hotel-detail-gallery-control hotel-detail-gallery-control--next'
                onClick={handleNextImage}
                type='button'
              >
                <span aria-hidden='true'>›</span>
              </button>
            </>
          ) : null}
          <button
            aria-label={alt ? `${alt} — ampliar` : 'Ampliar imagen'}
            className='absolute right-2 top-2 z-10 flex items-center justify-center rounded-full bg-black/40 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100'
            onClick={handleGalleryClick}
            type='button'
          >
            <svg
              aria-hidden='true'
              className='h-5 w-5'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              viewBox='0 0 24 24'
            >
              <path
                d='M15 3h6m0 0v6m0-6l-7 7M9 21H3m0 0v-6m0 6l7-7'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>
      </section>

      {previewIndex != null && (
        <HotelImagePreview
          alt={alt}
          images={images}
          initialIndex={previewIndex}
          onClose={handleClosePreview}
          open
        />
      )}
    </>
  );
}

function buildGalleryImages(hotel: HotelDetailData): string[] {
  const primaryImage = hotel.imageUrl.trim();
  const galleryImages = hotel.galleryImages.filter((src) => {
    const image = src.trim();
    return image && image !== primaryImage;
  });

  return galleryImages.length ? galleryImages : primaryImage ? [primaryImage] : [];
}

function buildMapEmbedUrl(lat: number, lng: number) {
  const delta = 0.005;
  const bbox = [lng - delta, lat - delta, lng + delta, lat + delta].join(',');

  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
}

interface HotelDetailLayoutProps {
  hotel: HotelDetailData;
  nights: number | null;
  checkIn: string;
  checkOut: string;
  galleryLabel: string;
  noImagesLabel: string;
  imageUnavailableLabel: string;
  t: (key: string) => string;
  canBook?: boolean;
  onBook?: (rateKey: string, rateName?: string) => void;
  ratePolicyModes?: Record<string, RatePolicyMode>;
  policyQuotesReady?: boolean;
}

function HotelDetailLayout({
  hotel,
  nights,
  galleryLabel,
  noImagesLabel,
  imageUnavailableLabel,
  t,
  canBook,
  onBook,
  ratePolicyModes,
  policyQuotesReady,
}: HotelDetailLayoutProps) {
  const { i18n } = useTranslation();
  const galleryImages = useMemo(() => buildGalleryImages(hotel), [hotel]);
  const priceLabel = formatHotelPrice(hotel.totalPrice, hotel.currency, i18n.language);

  return (
    <main className='mx-auto max-w-6xl px-4 py-8'>
      <article className='overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm'>
        <div className='lg:flex'>
          <div className='flex-1 min-w-0 p-6'>
            <div className='flex flex-wrap items-center gap-2'>
              <h1 className='text-2xl font-bold text-gray-900'>{hotel.name}</h1>
              {hotel.stars ? (
                <span className='rounded-full bg-amber-50 px-2 py-1 text-sm font-semibold text-amber-700'>
                  {hotel.stars}
                  {STAR_SYMBOL}
                </span>
              ) : null}
            </div>

            {hotel.destination ? (
              <p className='mt-1 text-sm capitalize text-gray-500'>{hotel.destination}</p>
            ) : null}

            <HotelDetailContent
              canBook={canBook}
              hotel={hotel}
              onBook={onBook}
              policyQuotesReady={policyQuotesReady}
              ratePolicyModes={ratePolicyModes}
            />

            {hotel.coordinates ? (
              <section
                aria-label={t('hotels.detail.location')}
                className='mt-6 border-t border-gray-200 pt-4'
              >
                <h3 className='text-sm font-semibold text-gray-700'>
                  {t('hotels.detail.location')}
                </h3>

                <div
                  aria-label={t('hotels.detail.mapPreviewTitle')}
                  className='mt-3 aspect-video w-full overflow-hidden rounded-md border border-gray-200'
                >
                  <iframe
                    className='h-full w-full border-0'
                    loading='lazy'
                    referrerPolicy='no-referrer'
                    src={buildMapEmbedUrl(hotel.coordinates.lat, hotel.coordinates.lng)}
                    title={t('hotels.detail.mapPreviewTitle')}
                  />
                </div>
              </section>
            ) : null}
          </div>

          <aside className='lg:w-80 lg:shrink-0 lg:border-l lg:border-gray-200 lg:bg-gray-50'>
            <div className='p-6'>
              <Gallery
                alt={hotel.name}
                galleryLabel={galleryLabel}
                imageUnavailableLabel={imageUnavailableLabel}
                images={galleryImages}
                nextImageLabel={t('hotels.detail.nextImage')}
                noImagesLabel={noImagesLabel}
                previousImageLabel={t('hotels.detail.previousImage')}
              />

              <div className='mt-6'>
                <span className='text-xs uppercase tracking-wide text-gray-500'>
                  {t('hotels.results.priceLegend')}
                </span>
                <p className='text-2xl font-bold text-gray-900'>
                  {priceLabel ?? t('hotels.results.rateUnavailable')}
                  {nights != null ? (
                    <span className='ml-2 text-base font-normal text-gray-500'>
                      / {nights} {t(nights === 1 ? 'hotels.night' : 'hotels.nights')}
                    </span>
                  ) : null}
                </p>
              </div>
            </div>
          </aside>
        </div>
      </article>
    </main>
  );
}

/**
 * One selection per option: its offer token plus one rate key per entry of the offer's rooms[]
 * (details stored before multi-room support carry a single provider key and the offer token in
 * `revalidation`).
 */
function toBookingSelection(
  rate: HotelDetailData['roomOptions'][number]['rates'][number],
  revalidation: NonNullable<HotelDetailData['revalidation']>,
): BookingSelection {
  return {
    key: rate.key,
    offerToken: rate.offerToken ?? revalidation.offerToken,
    rateKeys: rate.rateKeys ?? [rate.key],
  };
}

function collectBookingSelections(hotel: HotelDetailData): BookingSelection[] {
  const { revalidation } = hotel;
  if (!revalidation) return [];

  const selections = new Map<string, BookingSelection>();
  for (const rate of hotel.roomOptions.flatMap((option) => option.rates)) {
    if (rate.key && !selections.has(rate.key)) {
      selections.set(rate.key, toBookingSelection(rate, revalidation));
    }
  }
  return [...selections.values()];
}

function availabilityQuoteKey(hotel: HotelDetailData): string {
  const keys = collectBookingSelections(hotel).map((selection) => selection.key);
  return `${hotel.hotelId}|${hotel.revalidation?.searchToken ?? ''}|${keys.join(',')}`;
}

export function HotelDetailPage() {
  const { t } = useTranslation();
  const { hotelId } = useParams<{ hotelId: string }>();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const checkIn = params.get('checkIn') ?? '';
  const checkOut = params.get('checkOut') ?? '';
  const searchAdults = Math.max(1, Number(params.get('adults')) || 1);
  const searchChildren = Math.max(0, Number(params.get('children')) || 0);
  const cacheKey = useMemo(() => {
    const raw = new URLSearchParams(search).get('cacheKey');
    return raw || undefined;
  }, [search]);
  const nights = calculateNights(checkIn, checkOut);

  const booking = useHotelBooking();
  const { background: listingBackground, text: listingText } = useListingColors();
  const listingPrimaryButtonClass = `rounded-xl ${LISTING_PRIMARY_BUTTON_CLASS}`;
  const listingPrimaryLinkClass = `inline-block rounded-md px-4 py-2 text-sm ${LISTING_PRIMARY_BUTTON_CLASS}`;
  const isBookingActive = booking.status !== 'idle';
  const agencyEmail: string = useSelector(
    (state: any) => state?.applicationState?.templateData?.header?.emailContacto || '',
  );
  const agencyCountryId: number = useSelector(
    (state: any) => Number(state?.applicationState?.paisId) || 1,
  );

  const [detailState, setDetailState] = useState<HotelDetailState | null>(() =>
    hotelId ? readHotelDetailState(hotelId, cacheKey) : null,
  );

  // localStorage doesn't exist during SSR, so the server always renders with
  // detailState unresolved. Track hydration so we don't flash the "not found"
  // screen before the client has had a chance to read the real cached detail.
  const [isHydrated, setIsHydrated] = useState(false);

  // Re-read when hotelId or cacheKey changes (e.g. direct URL navigation)
  useEffect(() => {
    setDetailState(hotelId ? readHotelDetailState(hotelId, cacheKey) : null);
    setIsHydrated(true);
  }, [hotelId, cacheKey]);

  // Revalidation state (only relevant for expired-with-metadata paths)
  const [revalidationStatus, setRevalidationStatus] = useState<
    'idle' | 'loading' | 'success' | 'error' | 'missingMetadata'
  >('idle');
  const [revalidatedDetail, setRevalidatedDetail] = useState<HotelDetailData | null>(null);

  // Recurring expiry timer: whenever the current detail is fresh, schedule a
  // re-read after the remaining TTL so the refresh modal appears without a
  // page reload.  Covers both initial page load and post-revalidation cycles.
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (detailState?.status !== 'available') return;

    const remainingMs = hotelId ? getHotelDetailRemainingMs(hotelId, cacheKey) : null;

    // If the stored envelope is already expired or missing, sync state immediately.
    if (remainingMs === null || remainingMs <= 0) {
      setDetailState(hotelId ? readHotelDetailState(hotelId, cacheKey) : null);
      return;
    }

    const timer = setTimeout(() => {
      const fresh = hotelId ? readHotelDetailState(hotelId, cacheKey) : null;
      setDetailState(fresh);
      setRevalidationStatus('idle');
      setRevalidatedDetail(null);
    }, remainingMs);

    expiryTimerRef.current = timer;

    return () => {
      clearTimeout(timer);
      expiryTimerRef.current = null;
    };
  }, [detailState?.status, hotelId, cacheKey]);

  // Clean up any timer on unmount
  useEffect(() => {
    return () => {
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    };
  }, []);

  // Availability check on detail entry: same call the booking form used to make on
  // open, run ahead of time so its quote (token + per-passenger extraFields) is ready.
  const prefetchedQuoteKeyRef = useRef<string | null>(null);
  const { prefetchAvailability } = booking;

  useEffect(() => {
    if (detailState?.status !== 'available') return;

    const detail = detailState.detail;
    if (!canRevalidateDetail(detail) || !detail.revalidation) return;

    const quoteKey = availabilityQuoteKey(detail);
    if (prefetchedQuoteKeyRef.current === quoteKey) return;

    prefetchedQuoteKeyRef.current = quoteKey;
    void prefetchAvailability(detail.revalidation.searchToken, collectBookingSelections(detail));
  }, [detailState, prefetchAvailability]);

  const canRevalidate = !!(
    detailState?.status === 'expired' &&
    detailState.detail &&
    canRevalidateDetail(detailState.detail)
  );

  const handleBook = useCallback(
    (rateKey: string) => {
      const currentHotel =
        revalidationStatus === 'success' && revalidatedDetail
          ? revalidatedDetail
          : detailState && detailState.status !== 'missing'
            ? detailState.detail
            : null;
      if (!currentHotel?.revalidation) return;

      const rate = currentHotel.roomOptions.flatMap((o) => o.rates).find((r) => r.key === rateKey);
      if (!rate) return;

      const selection = toBookingSelection(rate, currentHotel.revalidation);

      void booking.initiateBooking(selection, currentHotel.revalidation, {
        agencyEmail,
        hotelName: currentHotel.name,
        destination: currentHotel.destination,
        checkin: checkIn,
        checkout: checkOut,
        totalPrice: rate.sale,
        currency: rate.currency ?? currentHotel.currency,
      });
    },
    [revalidationStatus, revalidatedDetail, detailState, booking, agencyEmail, checkIn, checkOut],
  );

  const handleRevalidate = useCallback(async () => {
    if (detailState?.status !== 'expired') return;

    const detail = detailState.detail;
    const r = detail.revalidation;
    if (!r) {
      setRevalidationStatus('missingMetadata');
      return;
    }

    setRevalidationStatus('loading');
    try {
      const ok = await prefetchAvailability(r.searchToken, collectBookingSelections(detail));
      if (!ok) {
        setRevalidationStatus('error');
        return;
      }
      prefetchedQuoteKeyRef.current = availabilityQuoteKey(detail);

      // Revalidation succeeded: refresh savedAt, persist, and update local state
      saveHotelDetail(detail);
      setRevalidatedDetail(detail);
      setDetailState({ status: 'available', detail });
      setRevalidationStatus('success');
    } catch {
      setRevalidationStatus('error');
    }
  }, [detailState, prefetchAvailability]);

  const handleNavigateToSearch = useCallback(() => {
    window.location.href = '/buscarxhoteles';
  }, []);

  if (!isHydrated) {
    return (
      <main aria-busy='true' className='mx-auto max-w-6xl px-4 py-8'>
        <div className='animate-pulse'>
          <div className='h-8 w-2/3 rounded bg-gray-200' />
          <div className='mt-6 h-64 w-full rounded-lg bg-gray-200' />
        </div>
      </main>
    );
  }

  if (!detailState || detailState.status === 'missing') {
    return (
      <main className='mx-auto max-w-3xl px-4 py-16 text-center'>
        <div className='rounded-xl bg-slate-100 p-8'>
          <h1 className='text-2xl font-bold text-gray-900'>{t('hotels.detail.notFound.title')}</h1>
          <p className='mt-4 text-gray-600'>{t('hotels.detail.notFound.message')}</p>
          <Link
            className={`mt-6 ${listingPrimaryLinkClass}`}
            style={listingPrimaryButtonStyle(listingBackground, listingText)}
            to='/buscarxhoteles'
          >
            {t('hotels.detail.notFound.backToSearch')}
          </Link>
        </div>
      </main>
    );
  }

  const showExpiredNoRevalidation =
    detailState.status === 'expired' && !canRevalidate && revalidationStatus !== 'error';

  if (showExpiredNoRevalidation) {
    return (
      <main className='mx-auto max-w-3xl px-4 py-16 text-center'>
        <div className='rounded-xl bg-amber-50 p-8 ring-1 ring-amber-200'>
          <h1 className='text-2xl font-bold text-amber-800'>{t('hotels.detail.expired.title')}</h1>
          <p className='mt-4 text-amber-700'>{t('hotels.detail.expired.message')}</p>
          <Link
            className='mt-6 inline-block rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700'
            to='/buscarxhoteles'
          >
            {t('hotels.detail.expired.backToSearch')}
          </Link>
        </div>
      </main>
    );
  }

  const hotel =
    revalidationStatus === 'success' && revalidatedDetail ? revalidatedDetail : detailState.detail;

  const galleryLabel = t('hotels.detail.gallery');
  const noImagesLabel = t('hotels.detail.noImages');
  const imageUnavailableLabel = t('hotels.detail.imageUnavailable');

  const canBook = canRevalidateDetail(hotel);

  const selectedRate = hotel.roomOptions
    .flatMap((o) => o.rates)
    .find((r) => r.key === booking.selectedRateKey);
  const selectedRateName = selectedRate?.name;
  const bookingRooms = resolveBookingRoomLayout(booking.availabilityRooms, selectedRate?.rooms, {
    adults: searchAdults,
    children: searchChildren,
  });
  // Remount the form if the quote changes the room layout, so the passenger list follows it.
  const bookingRoomsKey = bookingRooms
    .map((room) => `${room.id}:${room.adults}:${room.children}`)
    .join('|');

  const drawerOpen =
    booking.status === 'form' ||
    booking.status === 'submitting' ||
    booking.status === 'revalidating';

  return (
    <>
      <HotelDetailLayout
        canBook={canBook}
        checkIn={checkIn}
        checkOut={checkOut}
        galleryLabel={galleryLabel}
        hotel={hotel}
        imageUnavailableLabel={imageUnavailableLabel}
        nights={nights}
        noImagesLabel={noImagesLabel}
        onBook={handleBook}
        policyQuotesReady={booking.policyQuotesReady}
        ratePolicyModes={booking.ratePolicyModes}
        t={t}
      />

      {detailState.status === 'expired' &&
      canRevalidate &&
      revalidationStatus !== 'success' &&
      revalidationStatus !== 'error' &&
      !isBookingActive ? (
        <RefreshModalComponent
          confirmLabel={
            revalidationStatus === 'loading'
              ? t('hotels.detail.expired.verifying')
              : t('hotels.detail.expired.updateButton')
          }
          detail={t('hotels.detail.expired.updateDetail')}
          disabled={revalidationStatus === 'loading'}
          message={t('hotels.detail.expired.updateMessage')}
          onConfirm={handleRevalidate}
          open
          title={t('hotels.detail.expired.title')}
        />
      ) : null}

      {revalidationStatus === 'error' && !isBookingActive ? (
        <RefreshModalComponent
          confirmLabel={t('hotels.detail.expired.backToSearch')}
          detail=''
          message={t('hotels.detail.expired.revalidationFailed')}
          onConfirm={handleNavigateToSearch}
          open
          title={t('hotels.detail.expired.title')}
        />
      ) : null}

      {/* Booking availability check spinner */}
      {booking.status === 'revalidating' ? (
        <div
          aria-label={t('hotels.booking.checking')}
          className='fixed inset-0 z-[19000] flex items-center justify-center bg-black/20 backdrop-blur-[2px]'
        >
          <div className='flex items-center gap-3 rounded-2xl bg-white px-6 py-4 shadow-2xl'>
            <svg
              aria-hidden='true'
              className='h-5 w-5 animate-spin text-blue-600'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              />
              <path
                className='opacity-75'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                fill='currentColor'
              />
            </svg>
            <p className='text-sm font-medium text-gray-700'>{t('hotels.booking.checking')}</p>
          </div>
        </div>
      ) : null}

      {/* Booking form drawer — slides from right on desktop, bottom on mobile */}
      <Dialog
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: { xs: '100%', sm: 540 },
            height: { xs: '100%', sm: 'auto' },
            maxHeight: { xs: '100%', sm: '90vh' },
            m: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: '1.25rem' },
            overflow: 'hidden',
          },
        }}
        fullScreen={false}
        onClose={booking.cancelBooking}
        open={drawerOpen}
        sx={{ zIndex: 20000 }}
      >
        <HotelBookingForm
          agencyCountryId={agencyCountryId}
          cancellationPolicy={booking.cancellationPolicy}
          currency={selectedRate?.currency ?? hotel.currency}
          hotelName={hotel.name}
          initialData={booking.formDraft}
          isNonRefundable={booking.isNonRefundable}
          isSubmitting={booking.status === 'submitting'}
          key={bookingRoomsKey}
          onClose={booking.cancelBooking}
          onSubmit={booking.submitBooking}
          quoteRefreshNotice={booking.quoteRefreshNotice}
          quotedPrice={booking.quotedPrice}
          rateName={selectedRateName}
          rooms={bookingRooms}
          searchPrice={booking.searchPrice}
        />
      </Dialog>

      {/* Booking / inquiry success dialog */}
      <Dialog
        PaperProps={{
          sx: {
            borderRadius: '1.25rem',
            maxWidth: 360,
            width: '100%',
            mx: 2,
            overflow: 'hidden',
          },
        }}
        onClose={booking.cancelBooking}
        open={booking.status === 'success'}
        sx={{ zIndex: 20000 }}
      >
        {booking.isNonRefundable ? (
          <div className='px-8 py-8 text-center'>
            <div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100'>
              <CheckCircleOutlineIcon sx={{ fontSize: 28, color: '#059669' }} />
            </div>
            <h2 className='text-lg font-bold text-gray-900'>
              {t('hotels.booking.inquiry.success.title')}
            </h2>
            <p className='mt-2 text-sm text-gray-500'>
              {t('hotels.booking.inquiry.success.message')}
            </p>
            <button
              className={`mt-6 w-full py-3 text-sm ${listingPrimaryButtonClass}`}
              onClick={booking.cancelBooking}
              style={listingPrimaryButtonStyle(listingBackground, listingText)}
              type='button'
            >
              {t('hotels.booking.success.close')}
            </button>
          </div>
        ) : (
          <div className='px-8 py-8 text-center'>
            {booking.bookingOutcome === 'pending' ? (
              <>
                <div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100'>
                  <HourglassEmptyIcon sx={{ fontSize: 28, color: '#d97706' }} />
                </div>
                <h2 className='text-lg font-bold text-gray-900'>
                  {t('hotels.booking.success.pendingTitle')}
                </h2>
                <p className='mt-2 text-sm text-gray-500'>
                  {t('hotels.booking.success.pendingMessage')}
                </p>
              </>
            ) : (
              <>
                <div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100'>
                  <CheckCircleOutlineIcon sx={{ fontSize: 28, color: '#059669' }} />
                </div>
                <h2 className='text-lg font-bold text-gray-900'>
                  {t('hotels.booking.success.title')}
                </h2>
                <p className='mt-2 text-sm text-gray-500'>{t('hotels.booking.success.message')}</p>
              </>
            )}
            {booking.bookLocator ? (
              <p className='mt-3 text-sm text-gray-700'>
                {`${t('hotels.booking.success.locator')}: `}
                <span className='font-semibold'>{booking.bookLocator}</span>
              </p>
            ) : null}
            <button
              className={`mt-6 w-full py-3 text-sm ${listingPrimaryButtonClass}`}
              onClick={booking.cancelBooking}
              style={listingPrimaryButtonStyle(listingBackground, listingText)}
              type='button'
            >
              {t('hotels.booking.success.close')}
            </button>
          </div>
        )}
      </Dialog>

      {/* Booking error dialog */}
      <Dialog
        PaperProps={{
          sx: {
            borderRadius: '1.25rem',
            maxWidth: 360,
            width: '100%',
            mx: 2,
            overflow: 'hidden',
          },
        }}
        onClose={booking.cancelBooking}
        open={booking.status === 'error'}
        sx={{ zIndex: 20000 }}
      >
        <div className='px-8 py-8 text-center'>
          <div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100'>
            <ErrorOutlineIcon sx={{ fontSize: 28, color: '#dc2626' }} />
          </div>
          <h2 className='text-lg font-bold text-gray-900'>{t('hotels.booking.error.title')}</h2>
          <p className='mt-2 text-sm text-gray-500'>
            {t(booking.errorMessage || 'hotels.booking.error.bookingFailed')}
          </p>
          <div className='mt-6 flex gap-3'>
            <button
              className='flex-1 rounded-xl border border-gray-200 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50'
              onClick={booking.cancelBooking}
              type='button'
            >
              {t('hotels.booking.error.cancel')}
            </button>
            {booking.canRetryBooking ? (
              <button
                className={`flex-1 py-3 text-sm ${listingPrimaryButtonClass}`}
                onClick={booking.retryBooking}
                style={listingPrimaryButtonStyle(listingBackground, listingText)}
                type='button'
              >
                {t('hotels.booking.error.retry')}
              </button>
            ) : null}
          </div>
        </div>
      </Dialog>
    </>
  );
}
